<template>
	<app-title-bar
		:icon="['fas', 'fa-hand']"
		subtitle="Yard"
		title="Assign Pick / Load List"
		:badges="[contract?.contract_number, contract?.account_name]">
		<div class="flex gap-3">
			<app-button to="/yard" icon="fa-circle-left"> Back </app-button>

			<resource-details :loading="loading || !contract" :resource="contract" />

			<status-badge
				v-if="
					contract?.status && contract.status.length > 0 && contract.status[0].load_status
				"
				:status="contract.status[0]"
				status-key="load_status" />

			<status-badge
				v-else-if="contract?.status && contract.status.length > 0"
				:status="contract.status[0]"
				status-key="pick_status" />
		</div>
	</app-title-bar>

	<div class="flex flex-col-reverse gap-4">
		<app-panel class="grow" title="Item Details">
			<template #header>
				<div class="flex flex-col-reverse sm:flex-row items-center gap-3">
					<contract-notes v-if="contract?.status[0].notes">
						{{ contract?.status[0].notes }}
					</contract-notes>

					<contract-notes
						v-if="contract?.status[0].item_notes"
						title="Ancillary Items"
						button="Ancillaries">
						{{ contract?.status[0].item_notes }}
					</contract-notes>

					<operators-dropdown v-model="assignOperator" label="Assign Operators" />

					<div>
						<app-button
							size="small"
							group="left"
							theme="outline"
							@click="assignAll('pick')"
							>All Pick</app-button
						>
						<app-button
							size="small"
							group="right"
							theme="outline"
							@click="assignAll('load')"
							>All Load</app-button
						>
					</div>
				</div>
			</template>

			<AssignPickListTable
				ref="pickTable"
				:loading="loading"
				:items="items"
				@select="(value: ContractItem[]) => (selected = value)"
				@update="(value: ContractItem) => updateItem(value)" />

			<div v-if="canEdit">
				<div v-if="QARequired" class="flex">
					<div class="border rounded-md flex items-center py-1 px-3 mt-5">
						<app-checkbox
							v-model="qualityCheckConfirmed"
							name="quality_checked_by"
							wrapper="m-0"
							:true-value="true"
							:false-value="false"
							label="By submitting the pick confirmation, you are confirming you have completed the final quality check on the equipment, the equipment is ready for hire and has been inspected by a competent person in accordance with Mabey Hire Limited's Quality Management systems (to BS EN ISO 9001) and standard operating procedures" />
					</div>
				</div>

				<div class="flex flex-col lg:flex-row lg:items-center lg:justify-end mt-4 gap-3">
					<div
						v-if="selected.length > 0 && !allSelectedAreComplete"
						class="flex items-center">
						<app-alert compact class="!mb-0 !py-1.5" spacing="">
							<p>
								Only <app-badge theme="success">Complete</app-badge> items can be
								sent to load
							</p>
						</app-alert>
					</div>

					<div v-else class="flex flex-col lg:flex-row gap-3">
						<app-dropdown
							v-model="delType"
							size="large"
							:options="delTypeOptions"
							:disabled="loading || selected.length == 0"
							@selected="getVehicles()">
							Delivery Type
						</app-dropdown>

						<div class="flex">
							<depots-dropdown
								v-model="vehicleDepot"
								size="large"
								input-group="left"
								class="w-full lg:w-[100px]"
								:disabled="loading || !delType"
								@update:model-value="getVehicles" />

							<app-loader v-if="vehiclesLoading" />
							<app-dropdown
								v-if="!vehiclesLoading"
								v-model="vehicle"
								class="w-full lg:w-[300px]"
								size="large"
								:label-key="['code', 'name']"
								value-key="id"
								group-by="depot"
								input-group="right"
								:options="vehicleOptions"
								:disabled="loading || vehicleOptions.length == 0">
								Vehicle
							</app-dropdown>
						</div>

						<app-button :disabled="!canSendToLoad" @click="sendLoad">
							Send to Load
						</app-button>
					</div>

					<app-button
						:disabled="selected.length === 0 || !canSendToOperator"
						@click="sendToOperator">
						Send To Operator
					</app-button>
				</div>
			</div>
		</app-panel>
	</div>
</template>

<script lang="ts" setup>
	import { ref, computed, onMounted } from 'vue'
	import AssignPickListTable from '@/components/datatables/yard/pick/AssignPickListTable.vue'
	import ContractService from '@/services/contract'
	import ResourceDetails from '@/components/partials/panels/ResourceDetails.vue'
	import OperatorsDropdown from '@/components/partials/dropdowns/OperatorsDropdown.vue'
	import { formatItemsDates } from '@/utils/date'
	import useContracts from '@/composables/contracts'
	import { useRoute } from 'vue-router'
	import useOperatorStore from '@/stores/operators'
	import useToasterStore from '@/stores/toaster'
	import StatusBadge from '@/components/partials/cat2/StatusBadge.vue'
	import type { Contract } from '@/data/schema/contract'
	import type { ContractItem } from '@/data/schema/contract-item'
	import type { Row } from '@/data/types/datatable'
	import type { StatusUpdate } from '@/data/interfaces/status-update'
	import useAuthStore from '@/stores/auth'
	import useVehicles from '@/composables/depot'
	import DepotsDropdown from '@/components/partials/dropdowns/DepotsDropdown.vue'

	// Resources
	const api = new ContractService()
	const route = useRoute()
	const toaster = useToasterStore()
	const operatorStore = useOperatorStore()
	const authStore = useAuthStore()

	// Data
	const loading = ref(true)
	const contractNumber = ref()
	const deliveryMethod = ref()
	const contract = ref<Contract>()
	const items = ref<ContractItem[]>([])
	const assignOperator = ref('')
	const pickTable = ref()
	const selected = ref<ContractItem[]>([])
	const vehicleDepot = ref()
	const qualityCheckConfirmed = ref(false)

	const {
		delType,
		delTypeOptions,
		vehicle,
		vehicleOptions,
		vehiclesLoading,
		selectedVehicle,
		getVehicles,
		resetLoad
	} = useVehicles(
		() => contract.value,
		() => vehicleDepot.value
	)

	const { sendToLoad } = useContracts()

	const allSelectedAreComplete = computed(() => {
		if (!selected.value || selected.value.length == 0) {
			return false
		}

		return selected.value.every(
			(obj) =>
				obj.pick_status?.[0].status == 'Complete' ||
				obj.pick_status?.[0].status == 'Load In Progress'
		)
	})

	const canEdit = ref(false)
	const canSendToLoad = computed(() => {
		return (
			delType.value &&
			selected.value.length > 0 &&
			(selectedVehicle.value || delType.value == '2') &&
			allSelectedAreComplete.value
		)
	})

	const QARequired = computed(() => {
		return (
			contract.value?.status &&
			contract.value.status.length > 0 &&
			(contract.value.status[0].load_status === 1 ||
				contract.value.status[0].load_status === 2)
		)
	})

	const canSendToOperator = computed(() => {
		if (!contract.value?.status?.[0]) {
			return false
		}

		const allSelectedAssigned = selected.value.every(
			(obj) => obj.pick_status?.[0].assigned_to || obj.pick_status?.[0].load_assigned_to
		)

		const qualityCheckNeeded = QARequired.value && !qualityCheckConfirmed.value

		return allSelectedAssigned && !qualityCheckNeeded
	})

	/**
	 * Get contract and contract item data
	 */
	async function getData() {
		loading.value = true
		selected.value = []

		const { fetchData } = useContracts()

		try {
			const data = await fetchData(contractNumber.value, {
				withItems: false,
				withPickItems: true,
				withContractStatus: true,
				withPickStatus: true,
				depot: authStore.user?.current_depot,
				deliveryMethod: deliveryMethod.value
			})
			contract.value = data.contract

			items.value = formatItemsDates(data.items)

			items.value = items.value.map((item) => ({
				...item,
				stock_status: item.pick_status?.[0].status
			}))

			loading.value = false
		} catch (error) {
			console.error(error)
			loading.value = false
		}
	}

	/**
	 * Update selected items
	 */
	function sendToOperator() {
		const saveItems = selected.value.map((item) => ({
			id: item.id,
			status_id: item.pick_status?.[0].id,
			assigned_to: item.pick_status?.[0].assigned_to,
			load_assigned_to: item.pick_status?.[0].load_assigned_to,
			status: 1
		}))

		toaster.working({
			text: 'Saving contract item changes...',
			dismissable: false,
			timeout: 0
		})

		api.bulkUpdateStatus(
			saveItems as StatusUpdate[],
			'pick-status',
			false,
			qualityCheckConfirmed.value
		)
			.then(() => {
				toaster.clear()
				toaster.success({ text: 'Items saved successfully!' })
				getData()
			})
			.catch((error: any) => {
				toaster.clear()
				toaster.error({
					text: 'Error occurred updating contract items'
				})
				console.error(error)
			})
	}

	/**
	 * Call Contracts:SendToLoad composable function
	 */
	function sendLoad() {
		sendToLoad(selected.value, delType.value, selectedVehicle.value!).then(() => {
			getData()
			resetLoad()
		})
	}

	/**
	 * Set all contract items to assignOperator value
	 */
	function assignAll(process: 'load' | 'pick') {
		items.value.forEach((item) => {
			const key = process == 'load' ? 'load_assigned_to' : 'assigned_to'

			if (item.pick_status) {
				item.pick_status[0][key] = assignOperator.value
			}

			if (item.type == 'Kit' && item.kit_components) {
				item.kit_components.forEach((component) => {
					if (component.pick_status) {
						component.pick_status[0][key] = assignOperator.value
					}
				})
			}
		})
	}

	/**
	 * Filter items by ID
	 * @param id
	 */
	function getItemById(id: string) {
		return items.value?.filter((obj) => {
			return obj.id === id
		})[0]
	}

	/**
	 * Update a single item
	 * @param value
	 */
	function updateItem(value: Row) {
		let item = getItemById(value.id)

		if (item && item.pick_status) {
			item.pick_status[0].assigned_to = value.assigned_to
		}
	}

	onMounted(() => {
		operatorStore.set(authStore.user!.current_depot!)
		vehicleDepot.value = authStore.user!.current_depot
		contractNumber.value = route.params.id

		canEdit.value = authStore.isAbleTo(['edit-yard'])

		if (contractNumber.value) {
			getData()
		}
	})
</script>
